import { useState, useRef, ChangeEvent, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { CallbackFormLead } from './lead_form';

interface FormData {
  email: string;
  phoneNumber: string;
}

interface FormErrors {
  email: string;
  phoneNumber: string;
}

interface FormStatus {
  loading: boolean;
  success: boolean;
  error: string;
}

export const useContactUsForm = () => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    phoneNumber: '',
  });

  const [errors, setErrors] = useState<FormErrors>({
    email: '',
    phoneNumber: '',
  });

  const [formStatus, setFormStatus] = useState<FormStatus>({
    loading: false,
    success: false,
    error: '',
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validateEmail = (email: string): boolean => {
    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    let hasError = false;
    const newErrors: FormErrors = { email: '', phoneNumber: '' };

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      hasError = true;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasError = true;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required';
      hasError = true;
    } else {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
        hasError = true;
      }
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleChange =
    (field: keyof FormData) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    await CallbackFormLead({
      formData,
      setFormStatus,
      recaptchaRef,
    });
  };

  const resetForm = useCallback(() => {
    setFormData({ email: '', phoneNumber: '' });
    setErrors({ email: '', phoneNumber: '' });
    setFormStatus({ loading: false, success: false, error: '' });
    recaptchaRef.current?.reset();
  }, []);

  return {
    formData,
    errors,
    formStatus,
    recaptchaRef,
    handleChange,
    handleSubmit,
    resetForm,
  };
};
